import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { LOCAL_ID_PREFIX } from '../../constants';
import { updatePPNotes, updatePPPrivateNotes } from '../../store/actions/prescriptionPad';
import { getPrescriptionNotes, getPrivateNotes } from '../../store/selectors/prescriptionPad';
import { randomString } from '../../store/utils';
import PadV2PxElementCard from '../prescriptionPad/rightPanel/workspace/padV2/PadV2PxElementCard';
import onContainerClick from '../../pages/prescriptionPad/onClickContainer';
import { NotesIcon } from './Icons';
import TinyMCEditor from './TinyMceEditor';

const PPNotesIcon = NotesIcon();

const PRESCRIPTION_NOTES_HEADER_ID = 'prescription-notes-id';
export const PRESCRIPTION_NOTES_EDITOR = 'notes-editor';
const PRECRIPTION_PRIVATE_NOTES_HEADER_ID = 'prescription-private-notes-id';
const PRESCRIPTION_PRIVATE_NOTES_EDITOR = 'private-notes-editor';

const PPNotesCard: React.FC<{
    sectionName?: string;
}> = ({ sectionName }) => {
    const dispatch = useDispatch();

    const prescriptionNotes = useSelector(getPrescriptionNotes);
    const prescriptionPrivateNotes = useSelector(getPrivateNotes);

    const onPNotesSave = (text: string, parsedText: string): void => {
        dispatch(
            updatePPNotes(
                text
                    ? {
                          id: `${LOCAL_ID_PREFIX}${randomString(4)}`,
                          text: text,
                          parsedText: parsedText,
                      }
                    : undefined,
            ),
        );
    };

    const onPrivateNotesSave = (text: string, parsedText: string): void => {
        dispatch(
            updatePPPrivateNotes(
                text
                    ? {
                          id: `${LOCAL_ID_PREFIX}${randomString(4)}`,
                          text: text,
                          parsedText: parsedText,
                      }
                    : undefined,
            ),
        );
    };

    return (
        <div onFocus={onContainerClick}>
            <PadV2PxElementCard
                icon={PPNotesIcon}
                headingText={sectionName || 'Notes'}
                iconContainerBgClassName="bg-darwin-accent-green-800"
                bodyCssClassName=" rounded-8"
            >
                <div className="flex flex-auto items-end w-full overflow-auto pt-8">
                    <div className="flex w-50p h-full flex-col">
                        <p className="text-darwin-neutral-600 text-12 font-700 pb-8">
                            NOTES FOR PATIENT (TREATMENT/SURGICAL/OTHERS)
                        </p>
                        <div className="w-full h-full">
                            <TinyMCEditor
                                id={PRESCRIPTION_NOTES_EDITOR}
                                toolbarId={PRESCRIPTION_NOTES_HEADER_ID}
                                initialValue={
                                    prescriptionNotes?.text || prescriptionNotes?.parsedText
                                }
                                customOptions={{
                                    inline: true,
                                }}
                                onBlur={onPNotesSave}
                                max_chars={1000}
                                parentCssClasses={
                                    'flex flex-col tiny-mce overflow-auto border border-neutral-7 rounded-4 bg-white p-10 hide-scrollbar cursor-text focus-within:border-darwin-neutral-400 focus:outline-none min-h-140 max-h-140'
                                }
                            />
                        </div>
                    </div>
                    <div className="flex w-50p ml-16 h-full flex-col">
                        <div className="flex justify-between items-center pb-8">
                            <p className="text-darwin-neutral-600 text-12 font-700">
                                PRIVATE NOTES
                            </p>
                            <div className="flex items-center">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="12"
                                    height="13"
                                    viewBox="0 0 12 13"
                                    fill="none"
                                >
                                    <path
                                        d="M6 0.5C2.67188 0.5 0 3.19531 0 6.5C0 9.82812 2.67188 12.5 6 12.5C9.30469 12.5 12 9.82812 12 6.5C12 3.19531 9.30469 0.5 6 0.5ZM6 11.375C3.30469 11.375 1.125 9.19531 1.125 6.5C1.125 3.82812 3.30469 1.625 6 1.625C8.67188 1.625 10.875 3.82812 10.875 6.5C10.875 9.19531 8.67188 11.375 6 11.375ZM6.9375 8.375H6.5625V6.3125C6.5625 6.00781 6.30469 5.75 6 5.75H5.25C4.92188 5.75 4.6875 6.00781 4.6875 6.3125C4.6875 6.64062 4.92188 6.875 5.25 6.875H5.4375V8.375H5.0625C4.73438 8.375 4.5 8.63281 4.5 8.9375C4.5 9.26562 4.73438 9.5 5.0625 9.5H6.9375C7.24219 9.5 7.5 9.26562 7.5 8.9375C7.5 8.63281 7.24219 8.375 6.9375 8.375ZM6 5C6.39844 5 6.75 4.67188 6.75 4.25C6.75 3.85156 6.39844 3.5 6 3.5C5.57812 3.5 5.25 3.85156 5.25 4.25C5.25 4.67188 5.57812 5 6 5Z"
                                        fill="#727DA8"
                                    />
                                </svg>
                                <p className="text-12 text-darwin-neutral-600 ml-4">
                                    These will not be printed
                                </p>
                            </div>
                        </div>
                        <div className="w-full h-full">
                            <TinyMCEditor
                                id={PRESCRIPTION_PRIVATE_NOTES_EDITOR}
                                toolbarId={PRECRIPTION_PRIVATE_NOTES_HEADER_ID}
                                initialValue={
                                    prescriptionPrivateNotes?.text ||
                                    prescriptionPrivateNotes?.parsedText
                                }
                                customOptions={{
                                    inline: true,
                                }}
                                max_chars={1000}
                                onBlur={onPrivateNotesSave}
                                parentCssClasses={
                                    'flex flex-col tiny-mce overflow-auto border border-neutral-7 rounded-4 bg-white p-10 hide-scrollbar cursor-text focus-within:border-darwin-neutral-400 focus:outline-none min-h-140 max-h-140'
                                }
                            />
                        </div>
                    </div>
                </div>
            </PadV2PxElementCard>
        </div>
    );
};

export default PPNotesCard;
